/**
 * Catalog entitlements API (support / superadmin only).
 *
 * Wraps the PUT/DELETE/GET endpoints added in PR-B for managing project
 * plan_code, feature overrides and quota top-ups. The backend re-resolves
 * entitlements after every write, so each call returns the fresh
 * ResolvedProjectEntitlements payload — no client-side merge needed.
 */

import { globalController } from './controller';

/** GET /api/catalog/projects/:slug/entitlements */
export async function getProjectEntitlements(slug) {
  const res = await globalController.getGlobal(
    `/catalog/projects/${encodeURIComponent(slug)}/entitlements`,
  );
  return res?.data;
}

/** PUT /api/catalog/projects/:slug/entitlements — atomic save of full draft */
export async function saveProjectEntitlements(slug, payload) {
  const res = await globalController.put(
    `/catalog/projects/${encodeURIComponent(slug)}/entitlements`,
    payload,
  );
  return res?.data;
}

/** PUT /api/catalog/projects/:slug/plan */
export async function setProjectPlan(slug, planCode) {
  const res = await globalController.put(
    `/catalog/projects/${encodeURIComponent(slug)}/plan`,
    { plan_code: planCode },
  );
  return res?.data;
}

/** PUT /api/catalog/projects/:slug/features/:moduleCode */
export async function setProjectModuleOverride(slug, moduleCode, enabled, source = 'manual') {
  const res = await globalController.put(
    `/catalog/projects/${encodeURIComponent(slug)}/features/${encodeURIComponent(moduleCode)}`,
    { enabled, source },
  );
  return res?.data;
}

/** DELETE /api/catalog/projects/:slug/features/:moduleCode */
export async function clearProjectModuleOverride(slug, moduleCode) {
  const res = await globalController.delete(
    `/catalog/projects/${encodeURIComponent(slug)}/features/${encodeURIComponent(moduleCode)}`,
  );
  return res?.data;
}

/** PUT /api/catalog/projects/:slug/quotas/:quotaCode */
export async function setProjectQuotaOverride(slug, quotaCode, value) {
  const res = await globalController.put(
    `/catalog/projects/${encodeURIComponent(slug)}/quotas/${encodeURIComponent(quotaCode)}`,
    { value },
  );
  return res?.data;
}

/** DELETE /api/catalog/projects/:slug/quotas/:quotaCode */
export async function clearProjectQuotaOverride(slug, quotaCode) {
  const res = await globalController.delete(
    `/catalog/projects/${encodeURIComponent(slug)}/quotas/${encodeURIComponent(quotaCode)}`,
  );
  return res?.data;
}

/**
 * GET /api/catalog/registry/project-modules
 * Returns { modules: ProjectModuleDefinition[], plans: ProjectPlanDefinition[], quotas: QuotaDefinition[] }
 */
export async function getProjectModulesRegistry() {
  const res = await globalController.getGlobal(`/catalog/registry/project-modules`);
  return res?.data;
}
